import type { LeagueDynamicFieldValue } from "@/models/dynamicFieldValue";
import type { LeagueAthleteFieldDefinition } from "@/models/fieldDefinition";
import type { LeagueMemberSummary } from "@/models/memberSummary";
import type { LeagueAthleteValue, LeagueFieldSchema } from "@/models/schema";
import {
  isMembershipCurrentlyActive,
  resolvedLeagueID,
  type SBotUser,
  type SBotUserRole,
} from "@/models/user";

const STAFF_PROFILE_ROLES: ReadonlySet<SBotUserRole> = new Set([
  "admin",
  "leagueManager",
  "leagueStaff",
  "organizationManager",
  "organizationStaff",
]);

export interface AthleteProfileField {
  id: string;
  label: string;
  value: string;
}

export function athleteMatchesWorkspace(
  athlete: LeagueAthleteValue,
  leagueID: string,
  organizationID: string | null,
): boolean {
  return athlete.leagueID === leagueID
    && athlete.status === "active"
    && (!organizationID || athlete.organizationID === organizationID);
}

export function visibleAthleteProfileFields(
  athlete: LeagueAthleteValue,
  schema: LeagueFieldSchema | null,
  viewer: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  now = new Date(),
): AthleteProfileField[] {
  if (!schema || schema.leagueID !== athlete.leagueID) return [];

  return schema.fields
    .filter((field) => !field.isArchived)
    .sort((first, second) => first.sortOrder - second.sortOrder)
    .flatMap((field) => {
      if (!canViewAthleteField(field, athlete, viewer, isSBotLabsAdmin, now)) return [];
      const value = valueForAthleteField(field, athlete);
      const formatted = value ? formatAthleteFieldValue(value, field) : null;
      return formatted ? [{ id: field.id, label: field.label, value: formatted }] : [];
    });
}

export function publicAthleteHeadshotURL(
  athlete: LeagueAthleteValue,
  schema: LeagueFieldSchema | null,
): string | undefined {
  const field = schema?.fields.find(
    (candidate) => !candidate.isArchived
      && candidate.privacyScope === "public"
      && candidate.universalAthleteRole === "headshot-url",
  );
  if (!field) return undefined;
  const value = athlete.publicValues[field.id];
  return value?.kind === "string" && value.value.trim() ? value.value.trim() : undefined;
}

export function isStaffMemberSummary(member: LeagueMemberSummary): boolean {
  return member.roles.some((role) => STAFF_PROFILE_ROLES.has(role));
}

export function staffSummaryMatchesWorkspace(
  member: LeagueMemberSummary,
  leagueID: string,
  organizationID: string | null,
): boolean {
  return member.leagueID === leagueID
    && isStaffMemberSummary(member)
    && (!organizationID || member.organizationIDs.includes(organizationID));
}

export function hasActiveStaffProfileMembership(
  profile: SBotUser,
  leagueID: string,
  now = new Date(),
): boolean {
  return profile.memberships.some((membership) =>
    isMembershipCurrentlyActive(membership, now)
      && resolvedLeagueID(membership) === leagueID
      && (
        (membership.targetType === "league"
          && (membership.role === "admin" || membership.role === "leagueManager" || membership.role === "leagueStaff"))
        || (membership.targetType === "organization"
          && (
            membership.role === "admin"
            || membership.role === "organizationManager"
            || membership.role === "organizationStaff"
          ))
      ),
  );
}

function canViewAthleteField(
  field: LeagueAthleteFieldDefinition,
  athlete: LeagueAthleteValue,
  viewer: SBotUser | null | undefined,
  isSBotLabsAdmin: boolean,
  now: Date,
): boolean {
  switch (field.privacyScope) {
    case "public":
      return true;
    case "organization":
      return !!athlete.organizationID && !!viewer?.memberships.some((membership) =>
        isMembershipCurrentlyActive(membership, now)
          && membership.targetType === "organization"
          && resolvedLeagueID(membership) === athlete.leagueID
          && (membership.organizationID ?? membership.targetID) === athlete.organizationID,
      );
    case "league":
      return isSBotLabsAdmin || !!viewer?.memberships.some((membership) =>
        isMembershipCurrentlyActive(membership, now)
          && membership.targetType === "league"
          && resolvedLeagueID(membership) === athlete.leagueID
          && (membership.role === "admin" || membership.role === "leagueManager"),
      );
  }
}

function valueForAthleteField(
  field: LeagueAthleteFieldDefinition,
  athlete: LeagueAthleteValue,
): LeagueDynamicFieldValue | undefined {
  switch (field.privacyScope) {
    case "public":
      return athlete.publicValues[field.id];
    case "organization":
      return athlete.organizationValues[field.id];
    case "league":
      return athlete.leagueValues[field.id];
  }
}

function formatAthleteFieldValue(
  value: LeagueDynamicFieldValue,
  field: LeagueAthleteFieldDefinition,
): string | null {
  switch (value.kind) {
    case "string":
      return formatSelectOption(value.value.trim(), field);
    case "int":
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value.value);
    case "double":
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 }).format(value.value);
    case "bool":
      return value.value ? "Yes" : "No";
    case "date":
      return field.type === "date"
        ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" }).format(value.value)
        : new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }).format(value.value);
    case "stringList": {
      const joined = value.value
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => formatSelectOption(entry, field))
        .filter((entry): entry is string => entry !== null)
        .join(", ");
      return joined || null;
    }
  }
}

function formatSelectOption(value: string, field: LeagueAthleteFieldDefinition): string | null {
  if (!value) return null;
  if (field.type !== "singleSelect" && field.type !== "multiSelect") return value;
  return field.options.find((option) => option.id === value)?.label.trim() || value;
}
